import { useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Trash2, X } from "lucide-react";

const inputClass =
  "rounded border border-[#cccccc] bg-white px-2 py-1 text-sm text-[#323c44] hover:border-[#7d8b96] focus:border-[#57c1a9] focus:outline-none";
const iconButton =
  "rounded p-1 text-[#7d8b96] hover:bg-[#efefef] hover:text-[#119996] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#7d8b96]";

/** The pack as sections of report keys, in the order it reads. */
export function layoutFrom(sections = [], reports = []) {
  const layout = sections.map((name) => ({
    name,
    keys: reports.filter((r) => (r.section || "Reports") === name).map((r) => r.key),
  }));
  return layout;
}

/** What the reorder endpoint takes: every key in order, and each key's section. */
export function payloadFrom(layout) {
  const keys = [];
  const sections = {};
  for (const section of layout) {
    for (const key of section.keys) {
      keys.push(key);
      sections[key] = section.name;
    }
  }
  return { keys, sections };
}

/** Move an item one place up or down a list, returning a new list. */
function moved(list, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Rearrange the board pack: report order, which section each report sits in,
 * and the sections themselves.
 *
 * Saved as the reader's own changes, so an update to the default reports lays
 * this order back on rather than putting the old one back.
 */
export default function BoardPackArranger({ sections, reports, onSave, onCancel }) {
  const [layout, setLayout] = useState(() => layoutFrom(sections, reports));
  const [newSection, setNewSection] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const titles = Object.fromEntries(reports.map((r) => [r.key, r.title]));

  const moveReport = (sectionIndex, index, delta) => {
    setLayout((current) => {
      const next = current.map((s) => ({ ...s, keys: [...s.keys] }));
      const section = next[sectionIndex];
      const target = index + delta;
      if (target >= 0 && target < section.keys.length) {
        section.keys = moved(section.keys, index, delta);
        return next;
      }
      // Past the end of its section: step into the neighbouring one.
      const neighbour = next[sectionIndex + delta];
      if (!neighbour) return current;
      const [key] = section.keys.splice(index, 1);
      if (delta < 0) neighbour.keys.push(key);
      else neighbour.keys.unshift(key);
      return next;
    });
  };

  const moveToSection = (sectionIndex, index, targetName) => {
    setLayout((current) => {
      const next = current.map((s) => ({ ...s, keys: [...s.keys] }));
      const [key] = next[sectionIndex].keys.splice(index, 1);
      const target = next.find((s) => s.name === targetName);
      if (target) target.keys.push(key);
      return next;
    });
  };

  const renameSection = (sectionIndex, name) =>
    setLayout((current) => current.map((s, i) => (i === sectionIndex ? { ...s, name } : s)));

  const addSection = () => {
    const name = newSection.trim();
    if (!name || layout.some((s) => s.name === name)) return;
    setLayout((current) => [...current, { name, keys: [] }]);
    setNewSection("");
  };

  const handleSave = async () => {
    const names = layout.map((s) => s.name.trim());
    if (names.some((n) => !n)) {
      setError("Every section needs a name.");
      return;
    }
    if (new Set(names).size !== names.length) {
      setError("Two sections have the same name. Rename one, or move its reports into the other.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const trimmed = layout.map((s) => ({ ...s, name: s.name.trim() }));
      const { keys, sections: map } = payloadFrom(trimmed);
      await onSave(keys, map);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save the new order.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-md border border-[#57c1a9] bg-white p-5 print:hidden" data-testid="board-pack-arranger">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-sans text-base font-bold text-[#323c44]">Arrange the board pack</h3>
          <p className="mt-1 text-sm text-[#4a5b68]">
            Move reports up and down, into another section, or rename and reorder sections. An empty
            section disappears when you save.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#50e3c2] px-3 py-1.5 text-sm font-semibold text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50 uppercase tracking-[0.12em]"
            data-testid="save-arrangement"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save order
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-semibold text-[#7d8b96] hover:text-[#323c44]"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
        </div>
      </div>

      {error && <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="space-y-3">
        {layout.map((section, sectionIndex) => (
          <div key={sectionIndex} className="rounded border border-[#efefef] bg-[#f5f5f5]">
            <div className="flex items-center gap-2 border-b border-[#efefef] px-3 py-2">
              <input
                className={`${inputClass} flex-1 font-semibold`}
                value={section.name}
                onChange={(event) => renameSection(sectionIndex, event.target.value)}
                aria-label="Section name"
              />
              <button
                type="button"
                className={iconButton}
                disabled={sectionIndex === 0}
                onClick={() => setLayout((current) => moved(current, sectionIndex, -1))}
                title="Move section up"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                className={iconButton}
                disabled={sectionIndex === layout.length - 1}
                onClick={() => setLayout((current) => moved(current, sectionIndex, 1))}
                title="Move section down"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                className={iconButton}
                disabled={section.keys.length > 0}
                onClick={() => setLayout((current) => current.filter((_, i) => i !== sectionIndex))}
                title={section.keys.length ? "Move its reports out first" : "Remove section"}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <ul className="divide-y divide-[#efefef]">
              {section.keys.length === 0 && (
                <li className="px-3 py-2 text-xs text-[#7d8b96]">No reports yet.</li>
              )}
              {section.keys.map((key, index) => (
                <li key={key} className="flex items-center gap-2 bg-white px-3 py-2">
                  <span className="flex-1 truncate text-sm text-[#323c44]">{titles[key] || key}</span>
                  <select
                    className={`${inputClass} text-xs`}
                    value={section.name}
                    onChange={(event) => moveToSection(sectionIndex, index, event.target.value)}
                    aria-label={`Section for ${titles[key] || key}`}
                  >
                    {layout.map((s, i) => (
                      <option key={i} value={s.name}>
                        {s.name || "(unnamed)"}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={iconButton}
                    disabled={sectionIndex === 0 && index === 0}
                    onClick={() => moveReport(sectionIndex, index, -1)}
                    title="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className={iconButton}
                    disabled={sectionIndex === layout.length - 1 && index === section.keys.length - 1}
                    onClick={() => moveReport(sectionIndex, index, 1)}
                    title="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <input
          className={`${inputClass} w-64`}
          value={newSection}
          onChange={(event) => setNewSection(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") addSection();
          }}
          placeholder="New section name"
        />
        <button
          type="button"
          onClick={addSection}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
        >
          <Plus className="h-3.5 w-3.5" /> Add section
        </button>
      </div>
    </div>
  );
}
